import Loader from "../Loader";
import { format } from "date-fns";
import { useEffect } from "react";
import { Button } from "@mui/material";
import ProductCard from "./ProductCard";
import { useRecoilState } from "recoil";
import Alert from "@mui/material/Alert";
import { useCallback, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import { CartDataState } from "../../atoms/cartAtoms";
import { useGetCart } from "../../hooks/useManageCart";
import styles from "../../styles/ProductBundle.module.css";
import {
  useAddBundleToCart,
  useGetBundleForRetail,
} from "../../hooks/useMyRecommendations";
import { StyledLabelLight, StyledLabelRegular } from "../../styled";


const ProductBundle = () => {
  const { data: cartData } = useGetCart();
  const [cart, setCartData] = useRecoilState(CartDataState);
  const { data: bundle = [], isLoading } = useGetBundleForRetail();

  const [msg, setMsg] = useState("");
  const [validImage, setValidImage] = useState(true);
  const resetError = () => setMsg("");
  const handleSuccess = () => setMsg("Bundle added to cart successfully");
  const handleError = (errorMsg) => setMsg(errorMsg);
  const { mutate, isError, isSuccess } = useAddBundleToCart(
    handleSuccess,
    handleError
  );

  const bundleWithItems = bundle?.filter(
    (bundle) => bundle?.recommendationBundle?.items?.length > 0
  );

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return format(new Date(dateString), "MM/dd/yyyy");
  };

  const addBundleToCart = useCallback(
    (token) => {
      mutate(token);
    },
    [mutate]
  );

  useEffect(() => {
    if (cartData) {
      setCartData(cartData);
    }
  }, [cartData, setCartData]);

  if (isLoading) {
    return <Loader />;
  }

  if (!bundleWithItems || bundleWithItems.length === 0) {
    return null;
  }

  return (
    <>
      <section className={styles.titleContainer}>
        <h3>Your Skincare Professional’s Recommendations</h3>
      </section>
      {bundleWithItems.map((bundle) => {
        const {
          recommendationBundle,
          total,
          lastUserDate,
          isOrderPlaced,
          storeLogo,
          storeLabel,
        } = bundle;
        const { storeLabel: storeName } = storeLabel?.[0] || "";
        const { id, bundleName, items, token } = recommendationBundle;
        const bundleInCart =
          cart?.items?.length > 0 &&
          items?.every((bundleItem) =>
            cart.items.some((cartItem) => cartItem.sku === bundleItem.sku)
          );

        return (
          <section key={`Bundle-${id}`} className={styles.container}>
            <article className={styles.article}>
              {storeLogo && (
                <div className={styles.logoContainer}>
                  {validImage ? (
                    <img
                      src={storeLogo}
                      alt="Store Logo"
                      className={styles.logo}
                      onError={() => setValidImage(false)}
                    />
                  ) : (
                    <StyledLabelRegular className={styles.storeLabel}>
                      {storeName}
                    </StyledLabelRegular>
                  )}
                </div>
              )}

              <div className={styles.header}>
                <div className={styles.headerInfo}>
                  <h3 className={styles.bundleName}>{bundleName}</h3>
                  <StyledLabelLight>
                    {isOrderPlaced
                      ? `Last used on ${formatDate(lastUserDate)}`
                      : `Accessed on ${formatDate(lastUserDate)}`}
                  </StyledLabelLight>
                </div>
                <p className={styles.price}>
                  <span className="font-bold">${total ? `${total}` : 0}</span>
                </p>
              </div>

              <Button
                sx={{ mb: 2 }}
                onClick={() => addBundleToCart(token)}
                variant="btn-secondary"
                disabled={bundleInCart}
              >
                {bundleInCart ? "In Your Cart" : "Add Bundle to Cart"}
              </Button>
              <BundleItems products={items} cart={cart} />
            </article>
            <Snackbar
              onClose={resetError}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              autoHideDuration={6000}
              open={(isError || isSuccess) && msg !== ""}
            >
              <Alert
                severity={isError ? "error" : isSuccess ? "success" : "info"}
              >
                {msg}
              </Alert>
            </Snackbar>
          </section>
        );
      })}
    </>
  );
};

const BundleItems = ({ products = [], cart }) => {
  const [visibleItems, setVisibleItems] = useState(6);

  const showMore = () => {
    setVisibleItems((prev) => prev + 6);
  };

  const remainingItems = products.length - visibleItems;
  const displayedProducts = products.slice(0, visibleItems);

  return (
    <div>
      <div className={styles.productGrid}>
        {displayedProducts.map((product) => (
          <ProductCard key={product.sku} product={product} cart={cart} />
        ))}
      </div>

      {remainingItems > 0 && (
        <Button
          fullWidth
          className={styles.viewMore}
          onClick={showMore}
          aria-label="View more products"
        >
          View More ({remainingItems})
        </Button>
      )}
    </div>
  );
};

export default ProductBundle;
